import type { RowKey } from "./stopPlanning";
import { jobRowKey, newStopRowKey, parseRowKey } from "./stopPlanning";

/**
 * Map-selection sequence vocabulary (design §3, Phase 4).
 *
 * The scheduling map's staged selection is one ORDERED sequence of tokens — selection order
 * = run order (MC parity: the save walks the mixed selection with `currentOrder` = loop
 * index). Jobs share the row-key form (`job:<id>`); master dropoff pins select as
 * `dropoff:<id>` — deliberately NOT a row key, because a selected master dropoff only
 * becomes a `newstop:` row when it's assigned to a shift. Unprefixed strings parse as job
 * ids so legacy single-drag ids stay valid.
 */
export type SelectionToken = string;
export type ParsedSelectionToken = { kind: "job" | "dropoff"; id: string };

const JOB_PREFIX = "job:";
const DROPOFF_PREFIX = "dropoff:";

export function dropoffSelectionToken(dropoffId: string): SelectionToken {
	return `${DROPOFF_PREFIX}${dropoffId}`;
}

export function parseSelectionToken(token: SelectionToken): ParsedSelectionToken {
	if (token.startsWith(DROPOFF_PREFIX)) return { kind: "dropoff", id: token.slice(DROPOFF_PREFIX.length) };
	if (token.startsWith(JOB_PREFIX)) return { kind: "job", id: token.slice(JOB_PREFIX.length) };
	return { kind: "job", id: token };
}

/** Click-toggle: remove the token if present, else append it (selection order = click order). */
export function toggleSelectionToken(seq: readonly SelectionToken[], token: SelectionToken): SelectionToken[] {
	return seq.includes(token) ? seq.filter((t) => t !== token) : [...seq, token];
}

/** Bulk-add (lasso, range): append missing tokens in the given order; already-selected tokens
 *  keep their original position (re-lassoing never reorders an existing selection). */
export function appendSelectionTokens(
	seq: readonly SelectionToken[],
	tokens: readonly SelectionToken[],
): SelectionToken[] {
	const next = [...seq];
	for (const t of tokens) if (!next.includes(t)) next.push(t);
	return next;
}

/** Ordered ids per kind — the jobs-only views (acres, chips, membership bookkeeping) and the
 *  dropoff pin highlight both derive from the one sequence through this. */
export function selectionKindIds(seq: readonly SelectionToken[]): { jobIds: string[]; dropoffIds: string[] } {
	const jobIds: string[] = [];
	const dropoffIds: string[] = [];
	for (const t of seq) {
		const p = parseSelectionToken(t);
		(p.kind === "job" ? jobIds : dropoffIds).push(p.id);
	}
	return { jobIds, dropoffIds };
}

export interface SelectionRowKeyBlock {
	/** The insertion block, in selection order: `job:` keys + freshly-allocated `newstop:` keys. */
	rowKeys: RowKey[];
	/** One entry per dropoff token, aligned with its `newstop:` key. */
	newStops: Array<{ tempKey: string; dropoffId: string }>;
}

/** Convert a selection sequence into the mixed row-key block an assign inserts
 *  (`insertJobsOrdered` on row keys). Each dropoff token allocates a temp stop key via
 *  `allocTempKey` — the caller owns the counter and registers the returned stop metadata —
 *  UNLESS `existingStopKeyByDropoffId` (see {@link stopRowKeyByDropoffId}) already names a row
 *  for that dropoff on the destination: then the token reuses that row, which makes it the
 *  block's positional anchor and keeps the shift from gaining a second stop for a location it
 *  already carries. */
export function selectionToRowKeyBlock(
	tokens: readonly SelectionToken[],
	allocTempKey: () => string,
	existingStopKeyByDropoffId?: ReadonlyMap<string, RowKey>,
): SelectionRowKeyBlock {
	const rowKeys: RowKey[] = [];
	const newStops: Array<{ tempKey: string; dropoffId: string }> = [];
	for (const t of tokens) {
		const p = parseSelectionToken(t);
		if (p.kind === "job") {
			rowKeys.push(jobRowKey(p.id));
		} else {
			const existing = existingStopKeyByDropoffId?.get(p.id);
			if (existing) {
				rowKeys.push(existing);
				continue;
			}
			const tempKey = allocTempKey();
			newStops.push({ tempKey, dropoffId: p.id });
			rowKeys.push(newStopRowKey(tempKey));
		}
	}
	return { rowKeys, newStops };
}

/**
 * The row key each dropoff location already occupies in a shift's mixed sequence — committed
 * stops through their junction id, staged `newstop:` rows through their metadata. The FIRST row
 * wins when a shift carries the same location twice (a stop may legitimately be hit more than
 * once), so the anchor is the earliest one.
 *
 * This is what lets a dropoff pin clicked for a shift that already schedules it mean "here"
 * rather than "a second stop at the end of the run": the block built by
 * {@link selectionToRowKeyBlock} reuses the row, and `insertJobsOrdered` consolidates the whole
 * selection on its position (MC re-sets `Shift`+`Order` on an already-scheduled stop).
 */
export function stopRowKeyByDropoffId(
	sequence: readonly RowKey[],
	stops: readonly { id: string; dropoffId?: string }[],
	newStopMeta?: ReadonlyMap<string, { dropoffId: string }>,
): Map<string, RowKey> {
	const dropoffIdByStopId = new Map<string, string>();
	for (const stop of stops) if (stop.dropoffId) dropoffIdByStopId.set(stop.id, stop.dropoffId);
	const rowKeyByDropoffId = new Map<string, RowKey>();
	for (const key of sequence) {
		const parsed = parseRowKey(key);
		const dropoffId =
			parsed.kind === "stop"
				? dropoffIdByStopId.get(parsed.id)
				: parsed.kind === "newstop"
					? newStopMeta?.get(parsed.id)?.dropoffId
					: undefined;
		if (dropoffId && !rowKeyByDropoffId.has(dropoffId)) rowKeyByDropoffId.set(dropoffId, key);
	}
	return rowKeyByDropoffId;
}
